/**
 * Physical locations.
 *
 * Distinct from a department: a firm can have both, and the two are not a hierarchy —
 * one Family practice can sit across three offices. Unlike a department, an office is
 * NOT a permission scope; nobody's access widens or narrows because of the office they
 * are placed in.
 *
 * `timezone` matters more than it looks. It is what a court date recorded at an office
 * is read against, so an empty or wrong value here is a hearing in the wrong hour.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  usePaginatedList,
  type Office,
} from '@legalcrm/api';
import {
  Button,
  Form,
  FormActions,
  FormInput,
  FormTextarea,
  Modal,
  Table,
  useToast,
  useZodForm,
  type Column,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

import { Can } from '@/auth/permissions';
import { formatDate } from '@/lib/format';

const schema = z.object({
  name: z.string().min(1, 'Give the office a name'),
  address: z.string(),
  timezone: z.string(),
});

type Values = z.infer<typeof schema>;

export function OfficesPage() {
  const [editing, setEditing] = useState<Office | null>(null);
  const [creating, setCreating] = useState(false);

  const list = usePaginatedList<Office>(qk.list('offices'), STAFF.offices, { limit: 100 });

  const columns: Column<Office>[] = [
    {
      key: 'name',
      header: 'Office',
      render: (row) => <span className="text-ink">{row.name}</span>,
    },
    {
      key: 'address',
      header: 'Address',
      hideOnMobile: true,
      render: (row) => (
        <span className="whitespace-pre-line text-ink-muted">{row.address || '—'}</span>
      ),
    },
    {
      key: 'timezone',
      header: 'Timezone',
      hideOnMobile: true,
      render: (row) => <span className="font-mono text-sm text-ink-muted">{row.timezone || '—'}</span>,
    },
    {
      key: 'created_at',
      header: 'Created',
      hideOnMobile: true,
      align: 'right',
      render: (row) => <span className="tabular-nums">{formatDate(row.created_at)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Can module="organization" action="edit">
          <Button variant="ghost" size="sm" onClick={() => setEditing(row)}>
            Edit
          </Button>
        </Can>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-end">
        <Can module="organization" action="create">
          <Button variant="primary" onClick={() => setCreating(true)}>
            New office
          </Button>
        </Can>
      </div>

      <Table
        caption="Offices"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitle="No offices"
        emptyDescription="This firm has not recorded any locations."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
        }}
      />

      <OfficeDialog open={creating} onClose={() => setCreating(false)} office={null} />
      <OfficeDialog open={editing !== null} onClose={() => setEditing(null)} office={editing} />
    </>
  );
}

function OfficeDialog({
  open,
  onClose,
  office,
}: {
  open: boolean;
  onClose: () => void;
  office: Office | null;
}) {
  const api = useApi();
  const { toast } = useToast();

  const form = useZodForm(schema, {
    values: {
      name: office?.name ?? '',
      address: office?.address ?? '',
      timezone: office?.timezone ?? '',
    },
  });

  const save = useApiMutation<Office, Values>(
    {
      mutationFn: (values) => {
        // `address` and `timezone` are CharFields with `blank=True`, so an empty
        // string is the correct representation of "no address recorded" here — unlike
        // the UUID fields elsewhere, which need the key absent altogether.
        const body = { name: values.name, address: values.address, timezone: values.timezone };
        return office
          ? api.patch<Office>(`${STAFF.office(office.id)}/`, body)
          : api.post<Office>(`${STAFF.offices}/`, body);
      },
      invalidates: [qk.all('offices')],
    },
    {
      onSuccess: (saved) => {
        toast({
          title: office ? 'Office updated' : 'Office created',
          description: saved.name,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The change was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The office could not be saved. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal open={open} onClose={onClose} title={office ? 'Edit office' : 'New office'}>
      <Form
        form={form}
        ariaLabel={office ? 'Edit office' : 'New office'}
        error={save.error}
        onSubmit={(values) => save.mutateAsync(values)}
      >
        <FormInput form={form} name="name" label="Name" autoFocus autoComplete="off" />
        <FormTextarea form={form} name="address" label="Address" rows={3} />
        <FormInput
          form={form}
          name="timezone"
          label="Timezone"
          hint="An IANA name, e.g. Europe/London. What a hearing recorded at this office is read against."
        />
        <FormActions
          submitLabel={office ? 'Save office' : 'Create office'}
          busy={save.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
